import { DATETIME_FE_FORMAT, DATE_FE_FORMAT } from '@/constants';
import { Dayts, formatDollars, getClasses } from '@/utils';

import { Button } from 'react-bootstrap';
import Input from '@/components/Input';
import { InvoiceDocumentWithSummaryAndAmendments } from '@/api/services/invoices/searchInvoiceDocumentTable';
import { InvoiceDocumentWithSummaryGenDocumentStatusEnum } from '@/models/gen/graphql';
import { InvoiceHistoryTableProps } from '@/pages/InvoiceHistory/InvoiceHistoryTable';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';

export type InvoiceHistoryTableRowProps = InvoiceDocumentWithSummaryAndAmendments & {
  isSelected: boolean;
  expanded?: boolean;
  label?: string;
  toggleExpanded?: () => void;
  onSelect: InvoiceHistoryTableProps['onSelect'];
  onDownloadInvoice?: InvoiceHistoryTableProps['onDownloadInvoice'];
  onShowInvoiceActivity?: InvoiceHistoryTableProps['onShowInvoiceActivity'];
};

const InvoiceHistoryTableRow = ({
  amendment,
  invoiceId,
  invoiceDocumentId,
  invoiceDate,
  startDatetime,
  endDatetime,
  payerProviderDisplayName,
  airports,
  grandTotal,
  genDocumentStatus,
  dueDatetime,
  createdByFullName,
  createdAt,
  sentAt,
  sentByFullName,
  fileLocation,
  isSelected,
  expanded,
  label,
  toggleExpanded,
  onSelect,
  onDownloadInvoice,
  onShowInvoiceActivity,
}: InvoiceHistoryTableRowProps): JSX.Element => {
  // converts a datetime string that is in UTC to the local datetime string
  const sentAtFormatted = sentAt ? new Dayts(sentAt).toLocal().format(DATETIME_FE_FORMAT) : '';
  const airportList = airports?.replace(/,\s*/g, ', ');

  const formattedCreatedAt = new Dayts(createdAt).toLocal().format(DATETIME_FE_FORMAT);
  const [createdAtDate, createdAtTime] = formattedCreatedAt.split(' ');
  const hasDocuments = genDocumentStatus !== null;
  const didFail =
    (hasDocuments && genDocumentStatus === InvoiceDocumentWithSummaryGenDocumentStatusEnum.Failed) || genDocumentStatus === null;
  const isGenerating = genDocumentStatus && !didFail && genDocumentStatus !== InvoiceDocumentWithSummaryGenDocumentStatusEnum.Completed;
  let tooltipText = 'Download Latest Invoice';
  if (isGenerating) tooltipText = 'Generating Invoice...';
  if (!hasDocuments) tooltipText = 'No Invoice Available, Please Save To Try Again';
  if (didFail) tooltipText = 'Invoice Generation Failed, Please Save To Try Again';
  const isDisabled = !hasDocuments || isGenerating || didFail;

  return (
    <>
      <Table.Cell className={getClasses('SELECT', label === 'unamended' && 'alternate')}>
        {(label === 'most recent' || label === 'unamended') && (
          <Input type="checkbox" onChange={() => onSelect(`${invoiceId}`)} checked={isSelected ? true : false} />
        )}
      </Table.Cell>
      <Table.Cell className="EXPAND">
        {toggleExpanded && (
          <Button variant="icon" onClick={toggleExpanded}>
            <i className={getClasses('fa', expanded ? 'fa-chevron-down' : 'fa-chevron-right')} />
          </Button>
        )}
      </Table.Cell>
      <Table.Cell className="ID" data-status={label} data-suffix={label && label !== 'unamended' ? label : null}>
        {amendment || invoiceId}
      </Table.Cell>
      <Table.Cell className={getClasses('INVDATE', label === 'unamended' && 'alternate')}>
        {new Dayts(invoiceDate).format(DATE_FE_FORMAT)}
      </Table.Cell>
      <Table.Cell className={getClasses('START', label === 'unamended' && 'alternate')}>
        {new Dayts(startDatetime).format(DATE_FE_FORMAT)}
      </Table.Cell>
      <Table.Cell className={getClasses('END', label === 'unamended' && 'alternate')}>
        {new Dayts(endDatetime).format(DATE_FE_FORMAT)}
      </Table.Cell>
      <Table.Cell className="CLIENT" data-placeholder="--">
        {payerProviderDisplayName}
      </Table.Cell>
      <Table.Cell className="AIRPORT" data-placeholder="--">
        {airportList}
      </Table.Cell>
      <Table.Cell className={getClasses('AMOUNT', label === 'unamended' && 'alternate')} data-prefix="$">
        {formatDollars(grandTotal)}
      </Table.Cell>
      <Table.Cell className={getClasses('DUE', label === 'unamended' && 'alternate')}>
        {new Dayts(dueDatetime).format(DATE_FE_FORMAT)}
      </Table.Cell>
      <Table.Cell className="DOWNLOAD">
        <Tippy content={tooltipText}>
          <Button
            variant="icon"
            style={{ cursor: !isDisabled ? 'pointer' : 'default' }}
            onClick={() => (!isDisabled ? onDownloadInvoice(invoiceDocumentId) : undefined)}
          >
            <i
              className={getClasses(
                'fs-4',
                didFail ? 'fa fa-warning text-danger' : isGenerating ? 'fa fa-spinner fa-pulse opacity-25' : 'sv sv-download2'
              )}
            />
          </Button>
        </Tippy>
      </Table.Cell>
      <Table.Cell className="AMENDED" data-placeholder="--">
        {label !== 'original' && label !== 'unamended' && (
          <>
            <div>{createdAtDate}</div>
            <div className="opacity-50">{createdAtTime}</div>
          </>
        )}
      </Table.Cell>
      <Table.Cell className="SENT">
        {' '}
        {(label === 'most recent' || label === 'unamended') && (
          <Tippy content={`${sentAtFormatted ? `Last sent at ${sentAtFormatted}` : 'Not Yet Sent'}`}>
            <i
              className={getClasses('fa-regular fa-envelope pointer', !sentAtFormatted && 'opacity-50', sentAtFormatted && 'text-success')}
            ></i>
          </Tippy>
        )}
      </Table.Cell>
      <Table.Cell className="CREATOR" data-placeholder="--">
        {createdByFullName}
      </Table.Cell>
      <Table.Cell className="SENTBY" data-placeholder="--">
        {sentByFullName}
      </Table.Cell>
      <Table.Cell className="CREATED" data-placeholder="--">
        {(label === 'original' || label === 'unamended') && (
          <>
            <div>{createdAtDate}</div>
            <div className="opacity-50">{createdAtTime}</div>
          </>
        )}
      </Table.Cell>
      <Table.Cell className="ACTIVITY">
        <Tippy content="View Invoice Activity Log">
          <Button
            variant="icon"
            name="ACTIVITY_LOG"
            style={{ cursor: fileLocation ? 'pointer' : 'default' }}
            onClick={() => onShowInvoiceActivity(invoiceId)}
          >
            <i className="sv sv-bubble-dots fs-4" />
          </Button>
        </Tippy>
      </Table.Cell>
    </>
  );
};

export default InvoiceHistoryTableRow;
